/**
 * Owner Activity Models
 * 오너 활동 모니터링 관련 타입 정의
 */

export interface OwnerActivityLog {
  id: string;
  owner_id: string;
  organization_id: string;
  action_type: string;
  entity_type?: string;
  entity_id?: string;
  details?: Record<string, unknown>;
  ip_address?: string;
  created_at: string;
}

export interface InactiveOwner {
  owner_id: string;
  owner_email: string;
  owner_name?: string;
  organization_id: string;
  organization_name: string;
  last_activity_at?: string;
  days_inactive: number;
}

export interface OwnerActivitySummary {
  total_owners: number;
  active_owners_7d: number;
  inactive_owners_7d: number;
  inactive_owners_30d: number;
  total_logins_today: number;
  total_logins_7d: number;
}

export interface OwnerLastActivity {
  owner_id: string;
  organization_id: string;
  last_activity_at?: string;
  last_action_type?: string;
}

// Action type labels
export const ACTION_TYPE_LABELS: Record<string, string> = {
  login: '로그인',
  logout: '로그아웃',
  member_create: '회원 생성',
  member_update: '회원 수정',
  member_delete: '회원 삭제',
  settings_change: '설정 변경',
  seat_manage: '좌석 관리',
  schedule_manage: '스케줄 관리',
};

export const getActionTypeLabel = (actionType: string): string => {
  return ACTION_TYPE_LABELS[actionType] || actionType;
};

/**
 * 상대적 시간 형식으로 변환 (예: "3일 전", "2시간 전")
 */
export const formatRelativeTime = (dateString?: string): string => {
  if (!dateString) return '활동 기록 없음';

  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMinutes < 1) return '방금 전';
  if (diffMinutes < 60) return `${diffMinutes}분 전`;
  if (diffHours < 24) return `${diffHours}시간 전`;
  if (diffDays < 30) return `${diffDays}일 전`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)}개월 전`;
  return `${Math.floor(diffDays / 365)}년 전`;
};

/**
 * 비활성 기간에 따른 상태 색상
 */
export const getInactivityStatusColor = (daysInactive: number): string => {
  if (daysInactive >= 30) return 'text-red-600 bg-red-50';
  if (daysInactive >= 7) return 'text-yellow-600 bg-yellow-50';
  return 'text-green-600 bg-green-50';
};

/**
 * 비활성 상태인지 확인 (7일 기준)
 */
export const isInactiveOwner = (lastActivityAt?: string, thresholdDays = 7): boolean => {
  if (!lastActivityAt) return true;

  const lastActivity = new Date(lastActivityAt);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - lastActivity.getTime()) / (1000 * 60 * 60 * 24));

  return diffDays >= thresholdDays;
};
